import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NmButton } from './NmButton';
import { NmInput } from './NmInput';
import { coachAthlete, type AthleteLookup } from '../services/api';

interface AddAthleteModalProps {
  onClose: () => void;
  onSent?: () => void;
}

/**
 * Modal for coaches to find an athlete by mobile and send a coaching request.
 */
export function AddAthleteModal({ onClose, onSent }: AddAthleteModalProps) {
  const { t } = useTranslation();
  const [mobile, setMobile] = useState('');
  const [athlete, setAthlete] = useState<AthleteLookup | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  async function handleFind() {
    setError('');
    setSuccess('');
    setAthlete(null);
    const trimmed = mobile.trim();
    if (trimmed.length < 11) {
      setError(t('app.addAthlete.error.invalidMobile'));
      return;
    }
    setLoading(true);
    try {
      const res = await coachAthlete.findByMobile(trimmed);
      setAthlete(res.data.data.athlete);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('app.addAthlete.error.notFound');
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  async function handleSend() {
    if (!athlete) return;
    setError('');
    setSending(true);
    try {
      await coachAthlete.sendRequest(athlete.id);
      setSuccess(t('app.addAthlete.success'));
      onSent?.();
      window.setTimeout(onClose, 1200);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('app.addAthlete.error.sendFailed');
      setError(msg);
    } finally {
      setSending(false);
    }
  }

  return (
    <div style={s.backdrop} className="title-bar-no-drag" onClick={onClose}>
      <div className="modal-surface" style={s.panel} onClick={(e) => e.stopPropagation()}>
        <header style={s.header}>
          <h2 style={s.title}>{t('app.addAthlete.title')}</h2>
          <button type="button" onClick={onClose} style={s.closeBtn} aria-label={t('common.close')}>
            {t('common.close')}
          </button>
        </header>

        <p style={s.hint}>
          {t('app.addAthlete.hint')}
        </p>

        <div style={s.row}>
          <NmInput
            label={t('app.addAthlete.mobileLabel')}
            value={mobile}
            onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 11))}
            placeholder={t('app.addAthlete.mobilePlaceholder')}
            inputMode="numeric"
          />
          <NmButton
            variant="neutral"
            size="sm"
            className="add-athlete-modal__search"
            loading={loading}
            onClick={() => void handleFind()}
          >
            {t('app.addAthlete.find')}
          </NmButton>
        </div>

        {athlete && (
          <div style={s.preview}>
            <div style={s.avatar}>{athlete.full_name.charAt(0).toUpperCase()}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={s.previewName}>{athlete.full_name}</div>
              <div style={s.previewMobile}>{athlete.mobile}</div>
              {athlete.is_coached && (
                <div style={s.coachedNote}>{t('app.addAthlete.coachedNote')}</div>
              )}
            </div>
          </div>
        )}

        {error && <p style={s.error}>{error}</p>}
        {success && <p style={s.success}>{success}</p>}

        <footer style={s.footer}>
          <NmButton
            variant="neutral"
            size="sm"
            className="add-athlete-modal__action add-athlete-modal__action--cancel"
            onClick={onClose}
          >
            {t('common.cancel')}
          </NmButton>
          <NmButton
            variant="primary"
            size="sm"
            className="add-athlete-modal__action add-athlete-modal__action--send"
            disabled={!athlete}
            loading={sending}
            onClick={() => void handleSend()}
          >
            {t('app.addAthlete.sendRequest')}
          </NmButton>
        </footer>
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 4000,
    background: 'rgba(15, 23, 42, 0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  panel: {
    width: '100%',
    maxWidth: 420,
    background: 'var(--surface-0)',
    borderRadius: 'var(--r-4)',
    boxShadow: 'var(--nm-raised)',
    padding: '20px 22px 18px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  title: {
    margin: 0,
    fontSize: 16,
    fontWeight: 800,
    color: 'var(--fg-1)',
    fontFamily: 'var(--font-display)',
  },
  closeBtn: {
    border: 'none',
    background: 'none',
    fontSize: 22,
    lineHeight: 1,
    cursor: 'pointer',
    color: 'var(--fg-4)',
  },
  hint: {
    margin: '0 0 14px',
    fontSize: 12,
    color: 'var(--fg-3)',
    lineHeight: 1.5,
  },
  row: {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-end',
    marginBottom: 12,
  },
  preview: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    padding: '12px 14px',
    borderRadius: 'var(--r-3)',
    background: 'var(--surface-sunken)',
    marginBottom: 12,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, var(--vo2-pink-400), var(--vo2-pink-600))',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    flexShrink: 0,
  },
  previewName: {
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--fg-1)',
  },
  previewMobile: {
    fontSize: 11,
    color: 'var(--fg-4)',
    fontFamily: 'var(--font-mono)',
    marginTop: 2,
  },
  coachedNote: {
    fontSize: 10,
    color: 'var(--fg-3)',
    marginTop: 4,
    fontStyle: 'italic',
  },
  error: {
    margin: '0 0 10px',
    fontSize: 12,
    color: '#dc2626',
  },
  success: {
    margin: '0 0 10px',
    fontSize: 12,
    color: 'var(--vo2-green-600)',
  },
  footer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
    marginTop: 12,
    paddingTop: 12,
    borderTop: '1px solid color-mix(in srgb, var(--fg-4) 14%, transparent)',
  },
};
